import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Check,
  Eye,
  FileUp,
  Loader2,
  Pencil,
  RefreshCw,
  ShieldAlert,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { apiFetch, apiFetchBlob, apiUpload, getToken } from "@/api";
import { getPyqSubjects } from "@/data/curriculum";

/* Admin PYQ manager — upload ZIPs of question-paper PDFs (or single PDFs),
   review/correct the guessed metadata per paper, then publish to the student
   library. Unpublished papers are never visible to students. */

const BOARDS = ["CBSE", "ICSE"];
const KLASSES = ["10", "12"];
const EXAM_TYPES = ["Regular", "Compartment"];

const selectClass =
  "w-full rounded-lg border border-[#E3ECE6] bg-white px-2.5 py-1.5 text-xs font-semibold text-[#17201A] outline-none focus:border-[#16A34A]";

const PaperRow = ({ paper, onSaved, onDeleted }) => {
  const [draft, setDraft] = useState({ ...paper });
  const [saving, setSaving] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const dirty = JSON.stringify(draft) !== JSON.stringify(paper);

  const subjectOptions = (() => {
    if (!draft.board || !draft.klass) return [];
    const opts = getPyqSubjects(draft.board, draft.klass);
    return draft.subject && !opts.includes(draft.subject)
      ? [...opts, draft.subject]
      : opts;
  })();

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const payload = {
        board: draft.board,
        klass: draft.klass,
        subject: draft.subject,
        year: draft.year,
        examType: draft.examType,
        setCode: draft.setCode,
        ...extra,
      };
      const updated = await apiFetch(`/admin/pyq/papers/${paper.id}`, {
        method: "PATCH",
        body: payload,
      });
      setDraft({ ...updated });
      onSaved(updated);
      if (extra.published) toast.success(`Published "${paper.filename}"`);
      else if (extra.published === false) toast.success("Unpublished");
      else toast.success("Saved");
    } catch (e) {
      toast.error(e.message);
    }
    setSaving(false);
  };

  const saveRename = async () => {
    try {
      const updated = await apiFetch(`/admin/pyq/papers/${paper.id}`, {
        method: "PATCH",
        body: { displayName: nameDraft },
      });
      setDraft({ ...updated });
      onSaved(updated);
      setRenaming(false);
      toast.success("Display name updated");
    } catch (e) {
      toast.error(e.message);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${paper.filename}"? Students will lose access.`)) return;
    try {
      await apiFetch(`/admin/pyq/papers/${paper.id}`, { method: "DELETE" });
      onDeleted(paper.id);
      toast.success("Paper deleted");
    } catch (e) {
      toast.error(e.message);
    }
  };

  const preview = async () => {
    try {
      const blob = await apiFetchBlob(`/admin/pyq/papers/${paper.id}/file`);
      window.open(URL.createObjectURL(blob), "_blank");
    } catch (e) {
      toast.error(e.message);
    }
  };

  const incomplete = draft.missing?.length > 0;

  return (
    <tr
      data-testid={`pyq-row-${paper.id}`}
      className={`border-b border-[#F0F4F1] align-top last:border-0 ${
        !draft.published && incomplete ? "bg-[#FEF9C3]/30" : ""
      }`}
    >
      <td className="max-w-[260px] px-4 py-3">
        {renaming ? (
          <div className="flex items-center gap-1">
            <input
              data-testid={`row-rename-input-${paper.id}`}
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveRename();
                if (e.key === "Escape") setRenaming(false);
              }}
              placeholder={paper.filename}
              autoFocus
              maxLength={120}
              className={selectClass}
            />
            <button
              data-testid={`row-rename-save-${paper.id}`}
              onClick={saveRename}
              aria-label="Save display name"
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#16A34A] text-white hover:bg-[#15803D]"
            >
              <Check className="h-3.5 w-3.5" />
            </button>
            <button
              data-testid={`row-rename-cancel-${paper.id}`}
              onClick={() => setRenaming(false)}
              aria-label="Cancel rename"
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[#E3ECE6] text-[#647067] hover:border-[#DC2626]"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1.5">
            <p
              className="truncate text-xs font-bold text-[#17201A]"
              title={draft.displayName || paper.filename}
            >
              {draft.displayName || paper.filename}
            </p>
            <button
              data-testid={`row-rename-${paper.id}`}
              onClick={() => {
                setNameDraft(draft.displayName || "");
                setRenaming(true);
              }}
              aria-label="Edit display name"
              title="Edit displayed name (PDF file is untouched)"
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[#94A39B] transition-colors duration-200 hover:bg-[#F0F4F1] hover:text-[#16A34A]"
            >
              <Pencil className="h-3 w-3" />
            </button>
          </div>
        )}
        {draft.displayName && !renaming && (
          <p className="mt-0.5 truncate text-[11px] font-semibold text-[#94A39B]" title={paper.filename}>
            {paper.filename}
          </p>
        )}
        <p className="mt-1 text-[11px] font-semibold text-[#94A39B]">
          {incomplete ? (
            <span data-testid={`pyq-missing-${paper.id}`} className="text-[#B45309]">
              Needs: {draft.missing.join(", ")}
            </span>
          ) : (
            "Complete"
          )}
        </p>
      </td>
      <td className="px-2 py-3">
        <select
          data-testid={`row-board-${paper.id}`}
          value={draft.board || ""}
          onChange={(e) => set("board", e.target.value || null)}
          className={selectClass}
        >
          <option value="">Board…</option>
          {BOARDS.map((b) => (
            <option key={b} value={b}>{b}</option>
          ))}
        </select>
      </td>
      <td className="px-2 py-3">
        <select
          data-testid={`row-class-${paper.id}`}
          value={draft.klass || ""}
          onChange={(e) => set("klass", e.target.value || null)}
          className={selectClass}
        >
          <option value="">Class…</option>
          {KLASSES.map((k) => (
            <option key={k} value={k}>{k}</option>
          ))}
        </select>
      </td>
      <td className="min-w-[140px] px-2 py-3">
        <select
          data-testid={`row-subject-${paper.id}`}
          value={draft.subject || ""}
          onChange={(e) => set("subject", e.target.value || null)}
          disabled={!subjectOptions.length && !draft.subject}
          className={selectClass}
        >
          <option value="">Subject…</option>
          {subjectOptions.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </td>
      <td className="w-20 px-2 py-3">
        <input
          data-testid={`row-year-${paper.id}`}
          type="number"
          value={draft.year ?? ""}
          onChange={(e) => set("year", e.target.value ? Number(e.target.value) : null)}
          placeholder="Year"
          className={selectClass}
        />
      </td>
      <td className="px-2 py-3">
        <select
          data-testid={`row-type-${paper.id}`}
          value={draft.examType || ""}
          onChange={(e) => set("examType", e.target.value || null)}
          className={selectClass}
        >
          <option value="">Type…</option>
          {EXAM_TYPES.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </td>
      <td className="w-24 px-2 py-3">
        <input
          data-testid={`row-set-${paper.id}`}
          value={draft.setCode || ""}
          onChange={(e) => set("setCode", e.target.value)}
          placeholder="Set/code"
          className={selectClass}
        />
      </td>
      <td className="px-2 py-3">
        <span
          data-testid={`row-status-${paper.id}`}
          className={`inline-block rounded-full px-2.5 py-1 text-[11px] font-extrabold ${
            draft.published ? "bg-[#16A34A]/10 text-[#16A34A]" : "bg-[#F0F4F1] text-[#647067]"
          }`}
        >
          {draft.published ? "Published" : "Draft"}
        </span>
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            data-testid={`row-save-${paper.id}`}
            onClick={() => save()}
            disabled={!dirty || saving}
            className="rounded-full bg-[#17201A] px-3 py-1.5 text-[11px] font-bold text-white disabled:opacity-30"
          >
            Save
          </button>
          {draft.published ? (
            <button
              data-testid={`row-unpublish-${paper.id}`}
              onClick={() => save({ published: false })}
              disabled={saving}
              className="rounded-full border border-[#E3ECE6] px-3 py-1.5 text-[11px] font-bold text-[#647067] hover:border-[#FACC15]"
            >
              Unpublish
            </button>
          ) : (
            <button
              data-testid={`row-publish-${paper.id}`}
              onClick={() => save({ published: true })}
              disabled={saving}
              className="rounded-full bg-[#16A34A] px-3 py-1.5 text-[11px] font-bold text-white hover:bg-[#15803D] disabled:opacity-50"
            >
              Publish
            </button>
          )}
          <button
            data-testid={`row-view-${paper.id}`}
            onClick={preview}
            aria-label="Preview PDF"
            className="flex h-7 w-7 items-center justify-center rounded-full border border-[#E3ECE6] text-[#17201A] hover:border-[#16A34A]"
          >
            <Eye className="h-3.5 w-3.5" />
          </button>
          <button
            data-testid={`row-delete-${paper.id}`}
            onClick={remove}
            aria-label="Delete paper"
            className="flex h-7 w-7 items-center justify-center rounded-full border border-[#E3ECE6] text-[#DC2626] hover:border-[#DC2626]"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </td>
    </tr>
  );
};

const AdminPyq = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [denied, setDenied] = useState(false);
  const [papers, setPapers] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [publishingAll, setPublishingAll] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!getToken()) {
      navigate("/login");
      return;
    }
    apiFetch("/me")
      .then((u) => {
        setMe(u);
        if (!u.isAdmin) setDenied(true);
      })
      .catch(() => navigate("/login"));
  }, [navigate]);

  const load = useCallback(() => {
    apiFetch(`/admin/pyq/papers?status=${statusFilter}`)
      .then((d) => setPapers(d.papers))
      .catch(() => setDenied(true));
  }, [statusFilter]);

  useEffect(() => {
    if (me?.isAdmin) load();
  }, [me, load]);

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const r = await apiUpload("/admin/pyq/upload", file);
      toast.success(
        `Added ${r.addedCount} paper${r.addedCount === 1 ? "" : "s"}` +
          (r.duplicates ? ` · ${r.duplicates} duplicate${r.duplicates === 1 ? "" : "s"} skipped` : "") +
          (r.skipped?.length ? ` · ${r.skipped.length} skipped` : "")
      );
      if (r.skipped?.length) {
        r.skipped.slice(0, 3).forEach((s) => toast.warning(`${s.name}: ${s.reason}`));
      }
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) {
      toast.error(e.message);
    }
    setUploading(false);
  };

  const publishAll = async () => {
    setPublishingAll(true);
    try {
      const all = await apiFetch("/admin/pyq/papers?status=unpublished");
      const ids = all.papers.map((p) => p.id);
      if (!ids.length) {
        toast.info("No unpublished papers");
        return;
      }
      const r = await apiFetch("/admin/pyq/publish", { method: "POST", body: { ids } });
      toast.success(
        `Published ${r.published}` +
          (r.incomplete.length ? ` · ${r.incomplete.length} still incomplete` : "")
      );
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setPublishingAll(false);
    }
  };

  if (denied) {
    return (
      <div
        data-testid="adminpyq-denied"
        className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#F8FAF7] px-6 text-center"
      >
        <ShieldAlert className="h-10 w-10 text-[#DC2626]" />
        <h1 className="font-display text-2xl font-extrabold text-[#17201A]">Admins only</h1>
        <button
          data-testid="adminpyq-denied-home"
          onClick={() => navigate("/")}
          className="rounded-full bg-[#16A34A] px-6 py-2.5 text-sm font-bold text-white hover:bg-[#15803D]"
        >
          Back to home
        </button>
      </div>
    );
  }

  if (!me?.isAdmin) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F8FAF7]">
        <Loader2 className="h-7 w-7 animate-spin text-[#16A34A]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAF7] pb-16" data-testid="adminpyq-page">
      <header className="border-b border-[#E3ECE6] bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5">
          <div>
            <h1 data-testid="adminpyq-title" className="font-display text-2xl font-extrabold text-[#17201A]">
              PYQ Paper Library
            </h1>
            <p className="text-xs font-semibold text-[#647067]">
              Upload real question-paper PDFs, verify metadata, publish to students
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="adminpyq-back-admin"
              onClick={() => navigate("/admin")}
              className="rounded-full border border-[#E3ECE6] bg-white px-4 py-2 text-xs font-bold text-[#17201A] hover:border-[#16A34A]"
            >
              Admin Dashboard
            </button>
            <button
              data-testid="adminpyq-refresh"
              onClick={load}
              className="inline-flex items-center gap-2 rounded-full border border-[#E3ECE6] bg-white px-4 py-2 text-xs font-bold text-[#17201A] hover:border-[#16A34A]"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6">
        {/* Upload */}
        <section className="mt-8 rounded-2xl border border-dashed border-[#16A34A]/30 bg-white p-6">
          <h2 className="font-display text-lg font-extrabold text-[#17201A]">Upload papers</h2>
          <p className="mt-1 text-xs font-semibold text-[#647067]">
            A .zip of question-paper PDFs (e.g. organized in subject folders) or a
            single .pdf. Every PDF starts as an unpublished draft — review the
            guessed metadata below, then publish.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <input
              ref={fileRef}
              data-testid="adminpyq-file-input"
              type="file"
              accept=".zip,.pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="text-xs font-semibold text-[#647067] file:mr-3 file:rounded-full file:border-0 file:bg-[#F0F4F1] file:px-4 file:py-2 file:text-xs file:font-bold file:text-[#17201A] hover:file:bg-[#E3ECE6]"
            />
            <button
              data-testid="adminpyq-upload-btn"
              onClick={upload}
              disabled={!file || uploading}
              className="inline-flex items-center gap-2 rounded-full bg-[#16A34A] px-5 py-2.5 text-xs font-bold text-white hover:bg-[#15803D] disabled:opacity-40"
            >
              {uploading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <FileUp className="h-3.5 w-3.5" />
              )}
              {uploading ? "Uploading…" : "Upload"}
            </button>
          </div>
        </section>

        {/* Toolbar */}
        <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
          <div className="flex gap-2">
            {[
              ["all", "All"],
              ["unpublished", "Needs review"],
              ["published", "Published"],
            ].map(([v, label]) => (
              <button
                key={v}
                data-testid={`adminpyq-filter-${v}`}
                onClick={() => setStatusFilter(v)}
                className={`rounded-full border-2 px-4 py-2 text-xs font-bold transition-colors duration-200 ${
                  statusFilter === v
                    ? "border-[#16A34A] bg-[#16A34A]/10 text-[#17201A]"
                    : "border-[#17201A]/10 bg-white text-[#17201A]/60 hover:border-[#16A34A]/40"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <button
            data-testid="adminpyq-publish-all"
            onClick={publishAll}
            disabled={publishingAll}
            className="rounded-full bg-[#17201A] px-5 py-2.5 text-xs font-bold text-white hover:bg-[#16A34A] disabled:opacity-40"
          >
            {publishingAll ? "Publishing…" : "Publish all complete papers"}
          </button>
        </div>

        {/* Papers table */}
        <div className="mt-4 overflow-x-auto rounded-2xl border border-[#E3ECE6] bg-white">
          <table className="w-full min-w-[1100px] text-left text-sm">
            <thead>
              <tr className="border-b border-[#E3ECE6] text-[11px] font-bold uppercase tracking-[0.12em] text-[#647067]">
                <th className="px-4 py-3">Paper</th>
                <th className="px-2 py-3">Board</th>
                <th className="px-2 py-3">Class</th>
                <th className="px-2 py-3">Subject</th>
                <th className="px-2 py-3">Year</th>
                <th className="px-2 py-3">Exam Type</th>
                <th className="px-2 py-3">Set / Code</th>
                <th className="px-2 py-3">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(papers || []).map((p) => (
                <PaperRow
                  key={p.id}
                  paper={p}
                  onSaved={() => {}}
                  onDeleted={(id) => setPapers((ps) => ps.filter((x) => x.id !== id))}
                />
              ))}
              {papers && papers.length === 0 && (
                <tr>
                  <td
                    colSpan={9}
                    data-testid="adminpyq-empty"
                    className="px-5 py-10 text-center text-sm font-semibold text-[#94A39B]"
                  >
                    No papers here yet — upload a ZIP of question papers above.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {papers === null && (
            <div className="flex justify-center p-10" data-testid="adminpyq-loading">
              <Loader2 className="h-6 w-6 animate-spin text-[#16A34A]" />
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminPyq;
